import _ from "lodash";
import { takeLatest, call, put } from "redux-saga/effects";

import { authorizedRequest, downloadXLSFile, uploadFile } from "../../utils/api";
import { generateDefaultActions } from "../utils/helpers";

const DOWNLOAD_PROPERTIES = "DOWNLOAD_PROPERTIES";
const DOWNLOAD_PROPERTIES_SUCCESS = "DOWNLOAD_PROPERTIES_SUCCESS";
const DOWNLOAD_PROPERTIES_FAILURE = "DOWNLOAD_PROPERTIES_FAILURE";

const UPLOAD_PROPERTIES = "UPLOAD_PROPERTIES";
const UPLOAD_PROPERTIES_SUCCESS = "UPLOAD_PROPERTIES_SUCCESS";
const UPLOAD_PROPERTIES_FAILURE = "UPLOAD_PROPERTIES_FAILURE";

const CLEAR_UPLOAD_ERRORS = "CLEAR_UPLOAD_ERRORS";
const CLEAR_UPLOAD_ERRORS_SUCCESS = "CLEAR_UPLOAD_ERRORS_SUCCESS";

const DOWNLOAD_PROJECTIONS = generateDefaultActions("DOWNLOAD_PROJECTIONS");
const EXTRACT_PROJECTIONS = generateDefaultActions("UPLOAD_PROJECTIONS");
const CLEAR_EXTRACTED_PROJECTIONS = generateDefaultActions("CLEAR_EXTRACTED_PROJECTIONS");

// ACTION
export function downloadProperties(propertyIds) {
  return { type: DOWNLOAD_PROPERTIES, propertyIds };
}

export function uploadProperties(payload) {
  return { type: UPLOAD_PROPERTIES, ...payload };
}

export function clearUploadErrors() {
  return { type: CLEAR_UPLOAD_ERRORS };
}

export function downloadProjections(propertyId, edit, asOfDate, periodType, term) {
  return { type: DOWNLOAD_PROJECTIONS.value, propertyId, edit, asOfDate, periodType, term };
}

export function extractProjections(payload) {
  return { type: EXTRACT_PROJECTIONS.value, ...payload };
}

export function clearExtractedProjections() {
  return { type: CLEAR_EXTRACTED_PROJECTIONS.value };
}

// REDUCER
const DEFAULT_STATE = {
  recordsAvailable: false
};

export default function properties(state = DEFAULT_STATE, action) {
  const { type, payload } = action;
  switch (type) {
    case GET_ALL_PROPERTIES_SUCCESS:
      return {
        ...state,
        allProperties: payload.allProperties,
        assetManagers: payload.assetManagers,
        recordsAvailable: true
      };
    case DOWNLOAD_PROPERTIES_SUCCESS:
      return state;
    case DOWNLOAD_PROPERTIES_FAILURE:
      return state;
    case UPLOAD_PROPERTIES:
      return { ...state, showSpinner: true };
    case UPLOAD_PROPERTIES_SUCCESS:
      return { ...state, showSpinner: false, uploadErrorMessage: undefined, uploadSuccessful: true };
    case UPLOAD_PROPERTIES_FAILURE:
      return { ...state, showSpinner: false, uploadErrorMessage: payload.userFriendlyError, uploadSuccessful: false };
    case DOWNLOAD_PROJECTIONS.success:
      return state;
    case DOWNLOAD_PROJECTIONS.failure:
      return state;
    case EXTRACT_PROJECTIONS.value:
      return { ...state, showSpinner: true };
    case EXTRACT_PROJECTIONS.success:
      return { ...state, showSpinner: false, uploadErrorMessage: undefined, uploadSuccessful: true, extractedProjections: payload.result };
    case EXTRACT_PROJECTIONS.failure:
      return { ...state, showSpinner: false, uploadErrorMessage: payload.userFriendlyError, uploadSuccessful: false };
    case CLEAR_UPLOAD_ERRORS_SUCCESS:
      return { ...state, showSpinner: false, uploadErrorMessage: undefined, uploadSuccessful: undefined };
    case CLEAR_EXTRACTED_PROJECTIONS.success:
      return { ...state, extractedProjections: undefined };
    default:
      return state;
  }
}

const GET_ALL_PROPERTIES = "GET_ALL_PROPERTIES";
const GET_ALL_PROPERTIES_SUCCESS = "GET_ALL_PROPERTIES_SUCCESS";
const GET_ALL_PROPERTIES_FAILURE = "GET_ALL_PROPERTIES_FAILURE";
export function getAllProperties() {
  return { type: GET_ALL_PROPERTIES };
}
function* getAllPropertiesSaga() {
  try {
    const response = yield call(authorizedRequest, "/getAllProperties", "GET");
    yield put({ type: GET_ALL_PROPERTIES_SUCCESS, payload: response });
  } catch (error) {
    yield put({ type: GET_ALL_PROPERTIES_FAILURE, error });
  }
}

// SAGA's
function* downloadPropertiesSaga(payload) {
  try {
    const response = yield call(downloadXLSFile, "/downloadProperties", "POST", payload.propertyIds, "property-details.xlsx");
    yield put({ type: DOWNLOAD_PROPERTIES_SUCCESS, payload: response });
  } catch (error) {
    yield put({ type: DOWNLOAD_PROPERTIES_FAILURE, error });
  }
}

function* uploadPropertiesSaga(payload) {
  try {
    payload.url = "/properties";
    const response = yield call(uploadFile, payload, "PUT");
    yield put({ type: UPLOAD_PROPERTIES_SUCCESS, payload: response });
    yield* getAllPropertiesSaga();
  } catch (error) {
    const userFriendlyError = _.get(error, "response.data.error", "Upload encountered error. Please choose a different file and try again.");
    yield put({ type: UPLOAD_PROPERTIES_FAILURE, payload: { userFriendlyError, error } });
  }
}

function* downloadProjectionsSaga(payload) {
  try {
    const url = payload.edit ?
      `/propertyProjections/${payload.propertyId}?edit=${payload.edit}&asOfDate=${payload.asOfDate}&periodType=${payload.periodType}&term=${payload.term}` :
      `/propertyProjections/${payload.propertyId}`;
    const response = yield call(downloadXLSFile, url, "GET", undefined, "property-projections.xlsx");
    yield put({ type: DOWNLOAD_PROJECTIONS.success, payload: response });
  } catch (error) {
    yield put({ type: DOWNLOAD_PROJECTIONS.failure, error });
  }
}

function* extractProjectionsSaga(payload) {
  try {
    payload.url = "/extractPropertyProjections";
    const response = yield call(uploadFile, payload, "POST");
    yield put({ type: EXTRACT_PROJECTIONS.success, payload: response });
  } catch (error) {
    const userFriendlyError = _.get(error, "response.data.error", "Upload encountered error. Please choose a different file and try again.");
    yield put({ type: EXTRACT_PROJECTIONS.failure, payload: { userFriendlyError, error } });
  }
}

function* clearExtractedProjectionsSaga() {
  yield put({ type: CLEAR_EXTRACTED_PROJECTIONS.success });
}

function* clearUploadErrorsSaga() {
  yield put({ type: CLEAR_UPLOAD_ERRORS_SUCCESS });
}

export function* propertiesSaga() {
  yield takeLatest(DOWNLOAD_PROPERTIES, downloadPropertiesSaga);
  yield takeLatest(UPLOAD_PROPERTIES, uploadPropertiesSaga);
  yield takeLatest(DOWNLOAD_PROJECTIONS.value, downloadProjectionsSaga);
  yield takeLatest(EXTRACT_PROJECTIONS.value, extractProjectionsSaga);
  yield takeLatest(GET_ALL_PROPERTIES, getAllPropertiesSaga);
  yield takeLatest(CLEAR_UPLOAD_ERRORS, clearUploadErrorsSaga);
  yield takeLatest(CLEAR_EXTRACTED_PROJECTIONS.value, clearExtractedProjectionsSaga);
}
